import { DEPLOYMENT_NAME_TOKEN, EMPTY_DEPLOYMENT_NAME, dropEmptyDeploymentName } from './naming'

/**
 * Render a naming pattern against a real app — the mock's `renderPattern`
 * (sap-mockup/lib/naming.ts:85).
 *
 * F-9: the deploy wizard's Overview showed `{AppName}-{Arch}-{Version}` and a bare "group", i.e.
 * the pattern instead of the name. The names matter precisely because a GENERATED group does not
 * exist yet: it has no id to look up, so the pattern resolved against this app is the only thing
 * that can tell an admin who is about to be targeted. The backend renders the same names at
 * publish (`ensure_groups`, matched by name), so the two must agree — and showing the raw pattern
 * hides a disagreement rather than surfacing it.
 *
 * Unknown tokens are left verbatim rather than blanked. A pattern the UI cannot resolve is
 * information; silently emptying it would read as "no name". {DeploymentName} is the exception:
 * empty is the package's first deployment, so it renders empty with one separator next to it.
 */
export interface NameValues {
  appname?: string
  version?: string
  publisher?: string
  arch?: string
  intent?: string
  [k: string]: string | undefined
}

export function renderNamingPattern(pattern: string, values: NameValues): string {
  if (!pattern) return ''
  return dropEmptyDeploymentName(
    pattern.replace(/\{[^{}]*\}/g, (raw) => {
      const key = raw.slice(1, -1).trim().toLowerCase()
      const v = key === 'deploymentname' ? values[key]?.trim() : values[key]
      if (v != null && v !== '') return v
      return key === 'deploymentname' ? EMPTY_DEPLOYMENT_NAME : raw
    }),
  )
}

/**
 * How one deployment renders its names (`deployment_records.naming_facts`): its name, which
 * `{DeploymentName}` renders (null for the package's first deployment, whatever its name), whether the name is added
 * to every pattern without the token (set when it was created with names that clashed with another
 * deployment of the package), and whether its update-only app's groups render {Intent} as the
 * `UpdateOnly` literal, as deployments published before {Intent} followed the assignment do.
 */
export interface DeploymentNaming {
  name: string | null
  nameAdded: boolean
  updateGroupIntent?: 'UpdateOnly' | null
}

export const FIRST_DEPLOYMENT: DeploymentNaming = { name: null, nameAdded: false }

const NAMING_KEYS = ['appPattern', 'updateAppPattern', 'groupPattern', 'updateGroupPattern'] as const

export const usesDeploymentName = (pattern?: string | null): boolean =>
  (pattern ?? '').toLowerCase().includes(DEPLOYMENT_NAME_TOKEN.toLowerCase())

/** The name patterns a deployment renders: `-{DeploymentName}` is added to the end of every
 *  pattern without it when the deployment's name is added. naming_service.effective_naming. */
export function effectiveNaming<T extends Partial<Record<(typeof NAMING_KEYS)[number], string>>>(
  naming: T,
  deployment?: DeploymentNaming | null,
): T {
  if (!deployment?.name || !deployment.nameAdded) return { ...naming }
  const out: T = { ...naming }
  for (const key of NAMING_KEYS) {
    const pattern = out[key]
    if (pattern && !usesDeploymentName(pattern)) {
      ;(out as Record<string, string>)[key] = `${pattern}-${DEPLOYMENT_NAME_TOKEN}`
    }
  }
  return out
}

/** `vars` with the deployment's name for {DeploymentName}. */
export const withDeploymentVars = (
  vars: NameValues,
  deployment?: DeploymentNaming | null,
): NameValues => ({ ...vars, deploymentname: deployment?.name ?? '' })

/** The name a package's first deployment gets unless another is chosen, and its label when it
 *  was recorded without one. No later deployment may take it. */
export const FIRST_DEPLOYMENT_LABEL = 'Default'

/**
 * How a deployment is named on screen: its name, or Default for a first deployment recorded
 * without one. `_packageName` is accepted for callers that pass it and is not used.
 */
export function deploymentLabel(deploymentName: string | null | undefined, _packageName?: string): string {
  return deploymentName?.trim() || FIRST_DEPLOYMENT_LABEL
}

/** Longest deployment name. Short, so a group name carrying it still fits in the 64-character
 *  Entra mailNickname derived from it, and stays readable in the Intune console. */
export const DEPLOYMENT_NAME_MAX = 32

/** Intune caps a mobileApp displayName at 256 characters; Entra group names share the limit. */
export const INTUNE_NAME_MAX = 256

/**
 * ASCII letters, digits, space, `.`, `_` and `-`, starting and ending with a letter or digit.
 * ASCII only because the mailNickname Graph requires for a generated group keeps only
 * `[A-Za-z0-9_-]`; anything else would vanish from it. The backend applies the same pattern.
 */
const DEPLOYMENT_NAME_RE = /^[A-Za-z0-9](?:[A-Za-z0-9 ._-]*[A-Za-z0-9])?$/

/**
 * Why `name` cannot name a new deployment of a package whose deployments are named `taken`
 * (null for a first deployment recorded without a name), or null when it can. `first`: the name
 * of the package's first deployment, which may be Default. Uniqueness ignores case and
 * surrounding spaces, as the backend's check does.
 */
export function deploymentNameProblem(
  name: string,
  taken: (string | null | undefined)[],
  first = false,
): string | null {
  const n = name.trim()
  if (!n) return first ? 'Name the deployment.' : 'Name the new deployment; the package already has one.'
  if (n.length > DEPLOYMENT_NAME_MAX) {
    return `Use at most ${DEPLOYMENT_NAME_MAX} characters, so the name stays readable where it appears in Intune and Entra names.`
  }
  if (!DEPLOYMENT_NAME_RE.test(n)) {
    return 'Use letters, digits, spaces, dots, hyphens and underscores, starting and ending with a letter or digit.'
  }
  const lower = n.toLowerCase()
  if (!first && lower === FIRST_DEPLOYMENT_LABEL.toLowerCase()) {
    return `${FIRST_DEPLOYMENT_LABEL} is kept for the package's first deployment. Choose another name.`
  }
  if (taken.some((t) => (t ?? '').trim().toLowerCase() === lower)) {
    return `Another deployment of this package is named ${n}.`
  }
  return null
}

/** Title-case intent, as the mock's INTENT_TITLE does for the {Intent} token. */
export const intentToken = (intent: string): string =>
  intent ? intent.charAt(0).toUpperCase() + intent.slice(1) : intent

/**
 * What {Intent} renders on the update-only app's groups for a deployment published before {Intent}
 * followed the assignment there (`DeploymentNaming.updateGroupIntent`). Every other deployment
 * renders the row's own intent on both apps.
 */
export const UPDATE_INTENT_TOKEN = 'UpdateOnly'

interface GroupPatterns {
  groupPattern?: string
  updateGroupPattern?: string
}

interface GeneratedRow {
  intent: string
  target?: string
  groupSource?: string | null
}

/**
 * The group name each app object's pattern produces, for one app — the mock's `groupNameBuilders`
 * (sap-mockup/components/DeploymentTemplateWizard.tsx:202).
 *
 * `vars` carries the app being deployed, so the caller gets the name that will really be created
 * rather than the editor's sample one.
 */
export function groupNameBuilders(
  naming: GroupPatterns,
  vars: NameValues = {},
  deployment?: DeploymentNaming | null,
) {
  const patterns = effectiveNaming(naming, deployment)
  const named = withDeploymentVars(vars, deployment)
  const render = (pattern: string | undefined, intent: string) =>
    renderNamingPattern(pattern ?? '', { ...named, intent })
  return {
    app: (row: GeneratedRow) => render(patterns.groupPattern, intentToken(row.intent)),
    update: (row: GeneratedRow) =>
      render(
        patterns.updateGroupPattern,
        deployment?.updateGroupIntent === UPDATE_INTENT_TOKEN
          ? UPDATE_INTENT_TOKEN
          : intentToken(row.intent),
      ),
  }
}

/**
 * Every distinct group publish would create, in row order.
 *
 * Filtered on `groupSource === 'generated'` AND `target === 'group'` because that is the exact
 * condition `intune_template_service._rows` uses. Listing a name the backend will not create
 * would promise a group that never appears in the tenant.
 */
export function generatedGroupNames(
  source: {
    naming: GroupPatterns
    appAssignments: GeneratedRow[]
    updateAssignments: GeneratedRow[]
  },
  vars: NameValues = {},
  deployment?: DeploymentNaming | null,
): string[] {
  const name = groupNameBuilders(source.naming, vars, deployment)
  const isGenerated = (r: GeneratedRow) => r.groupSource === 'generated' && r.target === 'group'
  const names = [
    ...(source.appAssignments ?? []).filter(isGenerated).map(name.app),
    ...(source.updateAssignments ?? []).filter(isGenerated).map(name.update),
  ].filter(Boolean)
  return [...new Set(names)]
}

/**
 * The tokens a rendered name still carries, i.e. the ones no value was available for.
 *
 * `renderNamingPattern` deliberately leaves an unresolved token verbatim rather than dropping it,
 * so the name it returns can still be a pattern. A confirmation screen that shows that string as
 * though it were the finished name is asserting something untrue about what will be created, so
 * callers use this to mark the difference.
 */
export function unresolvedTokens(rendered: string): string[] {
  return Array.from(new Set(rendered.match(/\{[^{}]*\}/g) ?? []))
}

/** Split a rendered name into resolved text and the tokens that stayed unresolved. */
export function splitOnTokens(rendered: string): { text: string; unresolved: boolean }[] {
  return rendered
    .split(/(\{[^{}]*\})/g)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, unresolved: /^\{[^{}]*\}$/.test(part) }))
}
